<template>
  <div class="ds-page" v-if="!$store.getters.isForbidden">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Administration</div>
          <h2 class="page-title">Legal Documents</h2>
          <p class="page-subtitle">Manage PDP documents, content, publication status, and ownership.</p>
        </div>
        <div class="col-auto ms-auto">
          <button class="ds-btn ds-btn--blue" @click="modalCreateLegal = true">
            <font-awesome-icon icon="plus" />
            Create PDP
          </button>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="legal-page__toolbar">
        <div>
          <span class="legal-page__toolbar-title">PDP Directory</span>
          <span class="legal-page__toolbar-subtitle">{{ total_rows }} legal document{{ total_rows === 1 ? "" : "s" }}</span>
        </div>
        <div class="legal-page__toolbar-actions">
          <el-input
            v-model.trim="user.search_keyword"
            clearable
            size="small"
            placeholder="Search legal name..."
            class="legal-page__search-input"
            @clear="searchHandler"
            @keyup.enter.native="searchHandler"
          />
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.table" @click="searchHandler">
            <font-awesome-icon icon="search" /> Search
          </button>
          <button class="ds-btn ds-btn--secondary ds-btn--sm" :disabled="loading.table" @click="refreshList">
            <i class="el-icon-refresh"></i> Refresh
          </button>
        </div>
      </div>

      <div class="ds-table-wrap">
        <table class="ds-table" v-loading="loading.table">
          <thead>
            <tr>
              <th>Document</th>
              <th>Category</th>
              <th>Updated By</th>
              <th>Status</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in list_legal" :key="item._id">
              <td>
                <span class="legal-page__document-name">{{ item.name || "—" }}</span>
              </td>
              <td>
                <span class="ds-badge">{{ item.category || "—" }}</span>
              </td>
              <td>
                <div class="ds-user-cell">
                  <div class="ds-avatar ds-avatar--sm" :class="dsAvatarVariant(item.updated_by)">
                    {{ dsGetInitials(item.updated_by) }}
                  </div>
                  <span class="legal-page__updated-by">{{ item.updated_by || "—" }}</span>
                </div>
              </td>
              <td>
                <span class="ds-pill" :class="statusPillClass(item.status)">{{ item.status || "—" }}</span>
              </td>
              <td class="text-center">
                <el-dropdown
                  trigger="hover"
                  @command="handleActionCommand($event, item)"
                  popper-class="legal-page__action-popper"
                >
                  <button class="ds-btn ds-btn--secondary ds-btn--sm legal-page__action-trigger">
                    Actions <i class="el-icon-arrow-down el-icon--right"></i>
                  </button>
                  <el-dropdown-menu slot="dropdown" class="legal-page__action-menu">
                    <el-dropdown-item command="edit">
                      <font-awesome-icon icon="pen" class="text-primary" /> Update PDP
                    </el-dropdown-item>
                    <el-dropdown-item command="status" divided>
                      <font-awesome-icon icon="sliders-h" class="text-warning" /> Update Status
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </td>
            </tr>
          </tbody>
        </table>
        <el-empty
          description="No legal documents found"
          v-if="total_rows == 0"
          :image="emptyStateImage"
          class="ds-empty"
        ></el-empty>
      </div>
      <pagination :page="page" :per_page="per_page" :list="list_legal" :total_rows="total_rows" @pageChange="pageChangeHandler" />
    </div>

    <b-modal size="lg" id="add" title="Create PDP" hide-footer v-model="modalCreateLegal" @hide="onHide">
      <b-form @submit.prevent="createLegal" @reset="onHide">
        <b-form-group label="Name">
          <el-input placeholder="Name" v-model="legal.name" />
        </b-form-group>
        <b-form-group label="Effective Date">
          <el-date-picker
            @input="updateEndDatePickerOptions"
            :picker-options="datePickerStartOptions"
            class="w-full"
            v-model="legal.efective_date"
            type="date"
            placeholder="Pick effective date"
            format="dd MMMM yyyy"
            value-format="yyyy-MM-dd"
          />
        </b-form-group>
        <b-form-group label="Category">
          <el-select class="w-full" v-model="legal.category" placeholder="category">
            <el-option v-for="pdp in category_option" :key="pdp.id" :value="pdp.value" :label="pdp.name"> </el-option>
          </el-select>
        </b-form-group>
        <b-form-group label="Updated By">
          <el-input placeholder="Updated By" v-model="legal.updated_by" />
        </b-form-group>
        <b-form-group label="Description">
          <el-input placeholder="description" v-model="legal.description" />
        </b-form-group>
        <b-form-group label="Content">
          <quill-editor v-model="legal.content" ref="content" :options="editorOption" style="height: 150px">
          </quill-editor> </b-form-group
        ><br /><br /><br /><br />
        <div class="d-flex flex-row float-right">
          <el-button @click="createLegal" class="mr-2 ml-2" size="small" type="primary">Submit</el-button>
          <el-button @click="onHide" size="small" class="mr-2">Cancel</el-button>
        </div>
      </b-form>
    </b-modal>
    <b-modal size="lg" id="edit" title="Update PDP" hide-footer v-model="modalUpdateLegal" @hide="onHide">
      <b-form @submit.prevent="doUpdatePdp" @reset="onHide">
        <b-form-group label="Name">
          <el-input placeholder="Name" v-model="selected_transaction.name" />
        </b-form-group>
        <b-form-group label="Effective Date">
          <el-date-picker
            @input="updateEndDatePickerOptions"
            :picker-options="datePickerStartOptions"
            class="w-full"
            v-model="selected_transaction.efective_date"
            type="date"
            placeholder="Pick effective date"
            format="dd MMMM yyyy"
            value-format="yyyy-MM-dd"
          />
        </b-form-group>
        <b-form-group label="Updated By">
          <el-input placeholder="Updated By" v-model="selected_transaction.updated_by" />
        </b-form-group>
        <b-form-group label="Description">
          <el-input placeholder="description" v-model="selected_transaction.description" />
        </b-form-group>
        <b-form-group label="Content">
          <quill-editor v-model="selected_transaction.content" ref="content" :options="editorOption" style="height: 150px">
          </quill-editor> </b-form-group
        ><br /><br /><br /><br />
        <div class="d-flex flex-row float-right">
          <el-button @click="doUpdatePdp" class="mr-2 ml-2" size="small" type="primary">Submit</el-button>
          <el-button @click="onHide" size="small" class="mr-2">Cancel</el-button>
        </div>
      </b-form>
    </b-modal>
    <b-modal size="lg" id="edit" title="Update Status PDP" hide-footer v-model="modalUpdateLStatus" @hide="onHide">
      <b-form @submit.prevent="doUpdateStatus" @reset="onHide">
        <b-form-group label="Updated By">
          <el-input placeholder="Updated By" v-model="selected_transaction.updated_by" />
        </b-form-group>
        <b-form-group label="Status">
          <el-select class="w-full" v-model="selected_transaction.status" placeholder="status">
            <el-option v-for="pdp in status_option" :key="pdp.id" :value="pdp.value" :label="pdp.name"> </el-option>
          </el-select>
        </b-form-group>
        <div class="d-flex flex-row float-right">
          <el-button @click="doUpdateStatus" class="mr-2 ml-2" size="small" type="primary">Submit</el-button>
          <el-button @click="onHide" size="small" class="mr-2">Cancel</el-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>
<script>
import moment from "moment";
import { CREATE_LEGAL, GET_LIST_LEGAL, UPDATE_STATUS_LEGAL, UPDATE_LEGAL } from "@/store/modules/legal";
import { designSystemMixin } from "@/mixins/designSystemMixin";
import emptyStateImage from "@/assets/images/empty-state.png";
import popupErrorMessages from "@/library/popup-error-messages";
import Pagination from "../components/Pagination.vue";
import { quillEditor } from "vue-quill-editor";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
export default {
  components: { Pagination, quillEditor },
  mixins: [designSystemMixin],
  name: "Legal",
  metaInfo: {
    title: "Legal",
  },
  data() {
    return {
      selected_transaction: [],
      user: {
        search_by: "name",
        search_keyword: "",
        searchType: 1,
        searchUserTypeOptions: [
          { value: 1, text: "Private" },
          { value: 0, text: "Public" },
        ],
        search_options: [
          { value: "full_name", text: "Full Name" },
          { value: "email", text: "Email" },
          { value: "phone_number", text: "Phone Number" },
        ],
      },
      status_option: [
        {
          value: "draft",
          label: "Draft",
        },
        {
          value: "review",
          label: "Review",
        },
        {
          value: "publish",
          label: "Publish",
        },
      ],
      category_option: [
        {
          value: "PDP",
          label: "PDP",
        },
        {
          value: "OTHER",
          label: "OTHER",
        },
      ],
      legal: {
        name: "",
        description: "",
        updated_by: "",
        content: "",
        category: "",
        efective_date: "",
      },
      datePickerStartOptions: {
        disabledDate: (date) => moment(date).isBefore(moment(), "days"),
      },
      datePickerEndOptions: {
        disabledDate: (date) => moment(date).isBefore(moment(), "days"),
      },
      legal_edit: {
        name: "",
        description: "",
        updated_by: "",
        content: "",
        category: "",
        efective_date: "",
      },
      editorOption: {
        placeholder: "1. Legal Ewa  ...",
      },
      isLoadingHistory: false,
      emptyStateImage,
      modalCreateLegal: false,
      modalUpdateLegal: false,
      modalUpdateLStatus: false,
      closeModalLegal: false,
      showSearch: true,
      search_options: [
        { value: "full_name", text: "Name" },
        { value: "email", text: "Email" },
        { value: "phone_number", text: "Phone Number" },
      ],
      per_page: 10,
      size: 0,
      total_rows: 0,
      page: 1,
      list: [],
      data_pdp: [],
      isLoading: true,
      loading_detail: false,
      loader: null,
      search_keyword: "",
      search_by: "",
      list_legal: [],
      selected_item: {},
      loading: {
        summary_vendor: false,
        table: false,
      },
      search: {
        date: [moment().subtract(30, "day").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
        search_by: "full_name",
        search_keyword: "",
        start_date: moment().subtract(30, "day").format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
        status_filter: "",
      },
      datePickerOptions: {
        disabledDate: (date) => moment(date).isAfter(moment(), "days"),
      },
    };
  },
  computed: {
    showingFrom() {
      return this.total_rows ? (this.page - 1) * this.per_page + 1 : 0;
    },
    showingUntil() {
      if (this.total_rows) {
        if (this.list_legal.length < this.per_page) {
          return this.total_rows;
        }
        return this.page * this.per_page;
      }
      return 0;
    },
  },
  async mounted() {
    const loader = this.$loading.show();
    await this.getList();
    loader.hide();
  },
  methods: {
    statusPillClass(status) {
      const statusClass = {
        draft: "ds-pill--gray",
        review: "ds-pill--warning",
        publish: "ds-pill--success",
      };
      return statusClass[String(status).toLowerCase()] || this.dsStatusClass(status);
    },
    handleActionCommand(command, item) {
      if (command === "edit") this.updatePdp(item);
      if (command === "status") this.updateStatus(item);
    },
    async refreshList() {
      await this.getList();
    },
    async updatePdp(item) {
      this.selected_transaction = item;
      this.modalUpdateLegal = true;
    },
    async updateStatus(item) {
      this.selected_transaction = item;
      this.modalUpdateLStatus = true;
      this.loading = true;
    },
    async doUpdatePdp() {
      this.$confirm("You will update the Event. Make sure you have input the data correctly. Continue?", "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            done();
            const requestBody = {
              name: this.selected_transaction.name,
              updated_by: this.selected_transaction.updated_by,
              description: this.selected_transaction.description,
              content: this.selected_transaction.content,
              efective_date: this.selected_transaction.efective_date,
            };
            const id = this.selected_transaction.id;
            await this.$store
              .dispatch(UPDATE_LEGAL, { id, data: requestBody })
              .then(() => {
                this.$message({
                  title: "Success",
                  type: "success",
                  message: "PDP updated",
                });
                this.onHide();
                this.getList();
              })
              .catch((err) => {
                popupErrorMessages(err.response.data);
              });
            return;
          }
          done();
        },
      }).catch(() => {});
    },
    async doUpdateStatus() {
      this.$confirm("You will update the status. Make sure you have input the data correctly. Continue?", "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            done();
            const requestBody = {
              status: this.selected_transaction.status,
              updated_by: this.selected_transaction.updated_by,
            };
            const id = this.selected_transaction.id;
            await this.$store
              .dispatch(UPDATE_STATUS_LEGAL, { id, data: requestBody })
              .then(() => {
                this.$message({
                  title: "Success",
                  type: "success",
                  message: "Status updated",
                });
                this.onHide();
                this.getList();
              })
              .catch((err) => {
                popupErrorMessages(err.response.data);
              });
            return;
          }
          done();
        },
      }).catch(() => {});
    },
    async createLegal() {
      try {
        const requestBody = {
          name: this.legal.name,
          category: this.legal.category,
          updated_by: this.legal.updated_by,
          description: this.legal.description,
          content: this.legal.content,
          efective_date: this.legal.efective_date,
        };
        const response = await this.$store.dispatch(CREATE_LEGAL, { data: requestBody });
        this.legal.name = "";
        this.legal.category = "";
        this.legal.updated_by = "";
        this.legal.description = "";
        this.legal.content = "";
        this.legal.efective_date = "";
        this.data_pdp = response;
        this.$message({
          title: "Success",
          type: "success",
          message: "PDP created",
        });
        this.onHide();
        this.getList();
      } catch (err) {
        popupErrorMessages(err.response.data);
      }
    },
    pageChangeHandler(page) {
      this.page = page;
      this.getList();
    },
    searchHandler() {
      this.page = 1;
      this.getList();
    },
    searchHandlerGami() {
      this.search_by = this.user.search_by;
      this.search_keyword = this.user.search_keyword;
      this.searchType = this.user.searchType;
      this.getList();
    },
    onHide() {
      this.modalCreateLegal = false;
      this.modalUpdateLegal = false;
      this.modalUpdateLStatus = false;
    },
    async getList() {
      this.isLoading = true;
      this.loading.table = true;
      try {
        await this.$store.dispatch(GET_LIST_LEGAL, {
          page: this.page,
          query: {
            page: this.page,
            per_page: this.per_page,
            order_by: "created_at,desc",
            search_by: this.user.search_by,
            search_keyword: this.user.search_keyword,
          },
        });
        const { rows = [], count = 0 } = this.$store.getters.legal || {};
        this.total_rows = count;
        this.list_legal = rows;
      } catch (error) {
        this.list_legal = [];
        this.total_rows = 0;
      } finally {
        this.isLoading = false;
        this.loading.table = false;
      }
    },
    updateEndDatePickerOptions() {
      if (this.legal.efective_date) {
        const selectedStartDate = moment(this.legal.efective_date, "YYYY-MM-DD");
        this.datePickerEndOptions = {
          disabledDate: (date) => {
            const selectedDate = moment(date);
            const isDisabled = selectedDate.isBefore(moment(), "days") || selectedDate.isBefore(selectedStartDate, "day");
            return isDisabled;
          },
        };
        this.endDateKey += 1;
      }
    },
  },
};
</script>
<style>
.el-range-separator {
  width: 10% !important;
}

.child1-sticky {
  position: sticky;
  right: 0px;
  width: 40px;
  background: #fff;
}

.child2-sticky {
  position: sticky;
  right: 69px;
  width: 255px;
  background: #fff;
}

.scrollable-card-body {
  max-height: 400px;
  overflow-y: auto;
}

.el-timeline-item__timestamp {
  color: orange;
}

.custom-card {
  width: 500px;
  height: auto;
  margin-bottom: 10px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.legal-page__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(101, 109, 119, 0.16);
}

.legal-page__toolbar-title {
  display: block;
  color: #232e3c;
  font-size: 13.5px;
  font-weight: 700;
}

.legal-page__toolbar-subtitle,
.legal-page__updated-by {
  color: #656d77;
  font-size: 12px;
}

.legal-page__toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legal-page__search-input {
  width: 220px;
}

.legal-page__document-name {
  color: #232e3c;
  font-weight: 700;
}

.legal-page__action-trigger {
  min-width: 86px;
}

@media (max-width: 767.98px) {
  .legal-page__toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .legal-page__toolbar-subtitle {
    display: block;
    margin-top: 3px;
  }

  .legal-page__toolbar-actions {
    width: 100%;
  }

  .legal-page__search-input {
    flex: 1;
    min-width: 180px;
  }
}
</style>
